import { useRef, useCallback, useEffect } from 'react';
import { incrementUnread, clearUnread, setupFocusListener, isTabFocused } from '../utils/tabNotifications';

// Volume levels
const SEND_VOLUME = 0.15;
const RECEIVE_VOLUME_NORMAL = 0.2;
const RECEIVE_VOLUME_UNFOCUSED = 0.45;
const TYPING_VOLUME = 0.1;

/**
 * Hook to manage chat notification sounds and browser tab notifications.
 *
 * Features:
 * - Soft sounds for send/receive/typing
 * - Louder receive sound when tab not focused
 * - Tab title flashes with unread count
 * - Favicon shows red badge with count
 */
export function useChatSounds() {
  const sendAudioRef = useRef<HTMLAudioElement | null>(null);
  const receiveAudioRef = useRef<HTMLAudioElement | null>(null);
  const typingAudioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio elements on mount
  useEffect(() => {
    // Swap: send uses old receive sound, receive uses old send sound
    sendAudioRef.current = new Audio('/sounds/message-receive.mp3');
    receiveAudioRef.current = new Audio('/sounds/message-send.mp3');
    typingAudioRef.current = new Audio('/sounds/typing.mp3');

    // Preload the audio files
    sendAudioRef.current.load();
    receiveAudioRef.current.load();
    typingAudioRef.current.load();

    // Set default volumes
    sendAudioRef.current.volume = SEND_VOLUME;
    receiveAudioRef.current.volume = RECEIVE_VOLUME_NORMAL;
    typingAudioRef.current.volume = TYPING_VOLUME;

    // Pitch down the send sound slightly
    sendAudioRef.current.preservesPitch = false;
    sendAudioRef.current.playbackRate = 0.85;

    return () => {
      sendAudioRef.current = null;
      receiveAudioRef.current = null;
      typingAudioRef.current = null;
    };
  }, []);

  // Set up focus listener to clear tab indicators
  useEffect(() => {
    return setupFocusListener();
  }, []);

  const playSendSound = useCallback(() => {
    if (sendAudioRef.current) {
      sendAudioRef.current.currentTime = 0;
      sendAudioRef.current.play().catch((err) => {
        console.debug('Could not play send sound:', err.message);
      });
    }
  }, []);

  const playReceiveSound = useCallback(() => {
    if (receiveAudioRef.current) {
      // Louder when tab not focused to get attention
      receiveAudioRef.current.volume = isTabFocused() 
        ? RECEIVE_VOLUME_NORMAL 
        : RECEIVE_VOLUME_UNFOCUSED;
      receiveAudioRef.current.currentTime = 0;
      receiveAudioRef.current.play().catch((err) => {
        console.debug('Could not play receive sound:', err.message);
      });
    }
    // Update tab indicators
    incrementUnread();
  }, []);

  const playTypingSound = useCallback(() => {
    if (typingAudioRef.current) {
      typingAudioRef.current.currentTime = 0;
      typingAudioRef.current.play().catch((err) => {
        console.debug('Could not play typing sound:', err.message);
      });
    }
  }, []);

  return {
    playSendSound,
    playReceiveSound,
    playTypingSound,
    clearUnread,
    incrementUnread,
  };
}
